import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { TUploadedFile } from '@onesource/schemas';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import {
  MdAspectRatio,
  MdCategory,
  MdContentCopy,
  MdDelete,
  MdInsertDriveFile,
  MdLabel,
  MdPhotoSizeSelectLarge,
  MdSchedule,
  MdStorage,
} from 'react-icons/md';

import { useMediaDelete } from './use-media-delete';

export { CopyUrlMenu } from './copy-url-menu';
export type { TCopyUrlMenuProps } from './copy-url-menu';

function formatBytes(bytes: number): string {
  if (bytes === 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / 1024 ** i).toFixed(2)} ${units[i]}`;
}

function getMimeTypeLabel(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    pdf: 'application/pdf',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    json: 'application/json',
    txt: 'text/plain',
  };
  return map[ext] ?? `application/${ext}`;
}

function DetailRow(param: { icon: React.ReactNode; label: string; value: string; copyable?: boolean; mono?: boolean }) {
  const theme = useTheme();

  const { icon, label, value, copyable, mono } = param;

  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Box sx={{ mt: 0.25, color: theme.palette.text.disabled, flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.disabled,
            textTransform: 'uppercase',
            fontSize: '0.6rem',
            letterSpacing: 0.8,
            display: 'block',
            mb: 0.25,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              wordBreak: 'break-all',
              flex: 1,
              fontSize: '0.82rem',
              lineHeight: 1.5,
              fontFamily: mono ? 'monospace' : undefined,
            }}
          >
            {value}
          </Typography>
          {copyable && (
            <Tooltip title="Copy" placement="top" arrow>
              <IconButton
                size="small"
                sx={{
                  mt: '-2px',
                  p: 0.5,
                  flexShrink: 0,
                  color: theme.palette.text.disabled,
                  transition: 'color 0.15s, background-color 0.15s',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
                onClick={() => {
                  navigator.clipboard.writeText(value);
                  enqueueSnackbar('Copied to clipboard', { variant: 'success' });
                }}
              >
                <MdContentCopy size={14} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
}

type TMediaFileDetailsProps = {
  file: TUploadedFile;
  /** Called after the file is successfully deleted via the button below. */
  onDeleted?: () => void;
};

export function MediaFileDetails({ file, onDeleted }: TMediaFileDetailsProps) {
  const [dimensions, setDimensions] = useState<{ w: number; h: number } | null>(null);
  const { deleteFile, isPending: isDeleting } = useMediaDelete(onDeleted);

  useEffect(() => {
    setDimensions(null);
    if (!file.publicUrl) {
      return;
    }

    const isImage = /\.(jpe?g|png|gif|webp|svg|avif)$/i.test(file.filename);

    if (!isImage) {
      return;
    }

    const img = new Image();
    img.onload = () => setDimensions({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = file.publicUrl;
  }, [file.key, file.publicUrl, file.filename]);

  const aspectRatio = dimensions
    ? (() => {
        const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
        const d = gcd(dimensions.w, dimensions.h);
        return `${dimensions.w / d}:${dimensions.h / d}`;
      })()
    : null;

  const ext = file.filename.split('.').pop()?.toUpperCase() ?? '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <DetailRow icon={<MdLabel size={16} />} label="Filename" value={file.filename} copyable />

      <Divider />

      <DetailRow icon={<MdStorage size={16} />} label="File Size" value={formatBytes(file.size)} />

      <Divider />

      <DetailRow
        icon={<MdCategory size={16} />}
        label="File Type"
        value={`${ext} — ${getMimeTypeLabel(file.filename)}`}
      />

      {dimensions && (
        <>
          <Divider />
          <DetailRow
            icon={<MdPhotoSizeSelectLarge size={16} />}
            label="Dimensions"
            value={`${dimensions.w} × ${dimensions.h} px`}
          />
        </>
      )}

      {aspectRatio && (
        <>
          <Divider />
          <DetailRow icon={<MdAspectRatio size={16} />} label="Aspect Ratio" value={aspectRatio} />
        </>
      )}

      <Divider />

      <DetailRow
        icon={<MdSchedule size={16} />}
        label="Uploaded"
        value={new Date(file.lastModified).toLocaleString(undefined, {
          dateStyle: 'long',
          timeStyle: 'short',
        })}
      />

      <Divider />

      <DetailRow icon={<MdInsertDriveFile size={16} />} label="Storage Key" value={file.key} copyable mono />

      <Divider />

      <Button
        variant="soft"
        color="error"
        startIcon={<MdDelete size={18} />}
        onClick={() => deleteFile(file)}
        disabled={isDeleting}
        fullWidth
      >
        Delete file
      </Button>
    </Box>
  );
}
